import { useState, useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link, useNavigate } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useStreamingQuery } from '@/hooks/useStreamingQuery';
import { EditEbookDialog } from '@/components/EditEbookDialog';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ArrowLeft, BookMarked, Download, ExternalLink, Loader2, MessageSquare, MoreVertical, Pencil, Trash2, Copy } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { KIND_EBOOK } from '@/lib/pressstrKinds';
import { formatBytes } from '@/lib/blossomUpload';
import { nip19 } from 'nostr-tools';
import { CommentsSection } from '@/components/comments/CommentsSection';
import { Separator } from '@/components/ui/separator';
import { downloadFile } from '@/lib/downloadFile';

interface EbookPageProps {
  pubkey: string;
  identifier: string;
}

export function EbookPage({ pubkey, identifier }: EbookPageProps) {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const ebookFilters = useMemo(
    () => [{ kinds: [KIND_EBOOK], authors: [pubkey], '#d': [identifier], limit: 1 }],
    [pubkey, identifier],
  );
  const { events, isLoading } = useStreamingQuery(ebookFilters, ['ebook', pubkey, identifier]);

  const ebook: NostrEvent | undefined = events[0];
  const author = useAuthor(pubkey);
  const authorMeta = author.data?.metadata;

  const title = ebook?.tags.find(([n]) => n === 'title')?.[1] ?? 'Ebook';
  const image = ebook?.tags.find(([n]) => n === 'image')?.[1];
  const summary = ebook?.tags.find(([n]) => n === 'summary')?.[1];
  const url = ebook?.tags.find(([n]) => n === 'url')?.[1];
  const mimeType = ebook?.tags.find(([n]) => n === 'm')?.[1];
  const size = ebook?.tags.find(([n]) => n === 'size')?.[1];
  const ebookAuthors = ebook?.tags.filter(([n]) => n === 'author').map(([, v]) => v) ?? [];
  const topics = ebook?.tags.filter(([n]) => n === 't').map(([, v]) => v) ?? [];
  const lang = ebook?.tags.find(([n]) => n === 'l')?.[1];
  const isbn = ebook?.tags.find(([n]) => n === 'isbn')?.[1];
  const publishedAt = ebook?.tags.find(([n]) => n === 'published_at')?.[1];

  const isEpub = mimeType === 'application/epub+zip';
  const npub = nip19.npubEncode(pubkey);
  const naddr = nip19.naddrEncode({ kind: KIND_EBOOK, pubkey, identifier });
  const isOwner = user?.pubkey === pubkey;

  const copyNaddr = async () => {
    await navigator.clipboard.writeText(naddr).catch(() => {});
    toast({ title: 'Copied!', description: 'naddr copied to clipboard.' });
  };

  const ext = isEpub ? 'epub' : 'pdf';
  const handleDownload = async () => {
    if (!url || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadFile(url, `${title.replace(/[/\\:*?"<>|]/g, '_')}.${ext}`);
    } catch {
      toast({ title: 'Download failed', description: 'Could not download the file.', variant: 'destructive' });
    } finally {
      setIsDownloading(false);
    }
  };

  const publishDate = publishedAt
    ? new Date(parseInt(publishedAt) * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    : undefined;

  useSeoMeta({
    title: `${title} — PressStr`,
    description: summary ?? `Read ${title} on PressStr`,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6 space-y-6 max-w-4xl">
          <Skeleton className="h-8 w-32" />
          <div className="flex flex-col sm:flex-row gap-6">
            <Skeleton className="w-36 sm:w-48 aspect-[2/3] rounded-xl shrink-0 mx-auto sm:mx-0" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ebook) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="border-dashed max-w-md w-full">
          <CardContent className="py-16 text-center">
            <BookMarked className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Ebook not found.</p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" />Back to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Back nav */}
      <div className="border-b sticky top-0 bg-background/95 backdrop-blur z-30">
        <div className="container mx-auto px-4 h-12 flex items-center justify-between">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1" />Back</Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={copyNaddr}>
                <Copy className="w-4 h-4 mr-2" />Copy naddr
              </DropdownMenuItem>
              {isOwner && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setEditOpen(true)}>
                    <Pencil className="w-4 h-4 mr-2" />Edit Ebook
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />Delete Ebook
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
          {/* Cover */}
          <div className="w-36 sm:w-48 aspect-[2/3] rounded-xl overflow-hidden bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950 dark:to-slate-900 shrink-0 mx-auto sm:mx-0 shadow-lg">
            {image ? (
              <img src={image} alt={title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookMarked className="w-16 h-16 text-amber-400" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0 space-y-3 sm:space-y-4 text-center sm:text-left overflow-hidden">
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              <Badge className={isEpub ? 'bg-amber-600 text-white' : 'bg-red-600 text-white'}>
                {isEpub ? 'EPUB' : 'PDF'}
              </Badge>
              {lang && <Badge variant="outline">{lang.toUpperCase()}</Badge>}
              {topics.slice(0, 5).map(t => (
                <Badge key={t} variant="secondary">#{t}</Badge>
              ))}
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold">{title}</h1>

            {ebookAuthors.length > 0 && (
              <p className="text-base sm:text-lg text-muted-foreground italic">
                by {ebookAuthors.join(', ')}
              </p>
            )}

            {summary && <p className="text-muted-foreground break-words">{summary}</p>}
            {ebook.content && (
              <p className="text-muted-foreground text-sm break-all overflow-hidden line-clamp-6">{ebook.content}</p>
            )}

            {/* Metadata grid */}
            <div className="grid grid-cols-2 gap-3 text-sm py-1 text-left">
              {publishDate && (
                <div>
                  <p className="text-xs text-muted-foreground">Published</p>
                  <p className="font-medium">{publishDate}</p>
                </div>
              )}
              {size && (
                <div>
                  <p className="text-xs text-muted-foreground">File size</p>
                  <p className="font-medium">{formatBytes(parseInt(size))}</p>
                </div>
              )}
              {isbn && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">ISBN</p>
                  <p className="font-medium font-mono text-xs">{isbn}</p>
                </div>
              )}
            </div>

            {/* Download buttons */}
            {url && (
              <div className="flex flex-wrap gap-3 pt-1 justify-center sm:justify-start">
                <Button asChild size="lg" className="flex-1 sm:flex-none">
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-5 h-5 mr-2" />Read Online
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="flex-1 sm:flex-none" onClick={handleDownload} disabled={isDownloading}>
                  {isDownloading
                    ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Downloading…</>
                    : <><Download className="w-5 h-5 mr-2" />Download</>
                  }
                </Button>
              </div>
            )}

            {/* Uploader */}
            <div className="pt-2 border-t flex items-center gap-2 justify-center sm:justify-start">
              <Link to={`/${npub}`} className="shrink-0">
                <Avatar className="w-8 h-8 hover:opacity-80 transition-opacity">
                  <AvatarImage src={authorMeta?.picture} />
                  <AvatarFallback>{authorMeta?.name?.[0] ?? 'P'}</AvatarFallback>
                </Avatar>
              </Link>
              <div className="text-left">
                <p className="text-xs text-muted-foreground">Published by</p>
                <Link to={`/${npub}`} className="text-sm font-medium hover:text-primary transition-colors">
                  {authorMeta?.name ?? npub.slice(0, 20) + '…'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments */}
      {ebook && (
        <div className="container mx-auto px-4 max-w-4xl pb-10">
          <Separator className="mb-8" />
          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Discussion
            </h2>
            <CommentsSection
              root={ebook}
              emptyStateMessage="No comments yet"
              emptyStateSubtitle="Be the first to share your thoughts about this ebook!"
            />
          </section>
        </div>
      )}

      {ebook && (
        <>
          <EditEbookDialog open={editOpen} onOpenChange={setEditOpen} ebook={ebook} onSuccess={() => {}} />
          <ConfirmDeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} eventId={ebook.id} label="Ebook" onSuccess={() => navigate('/')} />
        </>
      )}
    </div>
  );
}
